import React, { useEffect, useRef, useState } from "react";
import { Button, message, Spin, Radio } from "antd";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./../style/common.css";

const SquareCardPayment = ({ amount, onSuccess }) => {
  const cardRef = useRef(null);
  const applePayRef = useRef(null);
  const [cardInstance, setCardInstance] = useState(null);
  const [applePayInstance, setApplePayInstance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [appleReady, setAppleReady] = useState(false);
  const [cardLoading, setCardLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState("apple");

  const appId = "sq0idp-X1EKrZj4nrq9JSVA_rtBCQ";
  const locationId = "L1FTNHJQ10EPQ";
  const navigate = useNavigate();

  useEffect(() => {
    const initializePaymentMethods = async () => {
      if (!window.Square) {
        message.error("Square SDK not loaded");
        return;
      }

      try {
        const payments = await window.Square.payments(appId, locationId);

        if (selectedMethod === "card") {
          const card = await payments.card();
          await card.attach("#card-container");
          setCardInstance(card);
          setCardReady(true);
          setCardLoading(false);
        }

        if (selectedMethod === "apple") {
          const paymentRequest = payments.paymentRequest({
            countryCode: "AU",
            currencyCode: "AUD",
            total: {
              amount: amount.toFixed(2),
              label: "Total",
            },
          });

          try {
            const applePay = await payments.applePay(paymentRequest);
            setApplePayInstance(applePay);
            setAppleReady(true);
          } catch (e) {
            console.warn("Apple Pay unsupported:", e.message);
            setAppleReady(false);
          }
        }
      } catch (err) {
        console.error("Payment method init failed:", err);
        message.error("Unable to initialize payment form.");
      }
    };

    initializePaymentMethods();
  }, [selectedMethod]);

  const handleCardPay = async () => {
    setLoading(true);
    try {
      const result = await cardInstance.tokenize();
      if (result.status !== "OK") {
        message.error("Card tokenization failed.");
        return;
      }
      await processPayment(result.token, "square_card");
    } catch (err) {
      console.error("Card Tokenization Error", err);
      message.error("Card payment failed.");
    }
    setLoading(false);
  };

  const handleApplePayClick = async () => {
    if (!applePayInstance) {
      message.error("Apple Pay is not ready.");
      return;
    }

    setLoading(true);
    try {
      const result = await applePayInstance.tokenize();
      if (result.status === "OK") {
        await processPayment(result.token, "apple_pay");
      } else {
        console.warn("Apple Pay Tokenization failed", result);
        message.error("Apple Pay failed.");
      }
    } catch (err) {
      console.error("Apple Pay Error", err);
      message.error("Apple Pay processing failed.");
    }
    setLoading(false);
  };

  const processPayment = async (nonce, paymentMode) => {
    const token = localStorage.getItem("token");
    const storedOrder = localStorage.getItem("pending_order_payload");

    if (!storedOrder) {
      message.error("Order data missing.");
      return;
    }

    try {
      const payload = {
        ...JSON.parse(storedOrder),
        amount: amount.toFixed(2),
        nonce,
        payment_mode: paymentMode,
      };

      const res = await axios.post(
        "https://cafemandhurah.mandurahcafe.com.au/api/create-payment-and-order",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data?.success) {
        onSuccess();
      } else {
        message.error(res.data?.message || "Payment failed.");
      }
    } catch (err) {
      console.error("Payment processing failed:", err);
      message.error("Payment & Order failed.");
    }
  };

  return (
    <div className="card-payment-wrapper">
      <Radio.Group
        value={selectedMethod}
        onChange={(e) => setSelectedMethod(e.target.value)}
        style={{ marginBottom: 16 }}
      >
        <Radio value="apple">Apple Pay</Radio>
        <Radio value="card">Card</Radio>
      </Radio.Group>

      {/* Apple Pay Button */}
      {selectedMethod === "apple" && (
        <>
          <div
            id="apple-pay-button"
            ref={applePayRef}
            style={{
              height: "48px",
              width: "100%",
              display: "inline-block",
              WebkitAppearance: "-apple-pay-button",
              applePayButtonType: "plain",
              applePayButtonStyle: "black",
              marginBottom: "20px",
            }}
            onClick={handleApplePayClick}
          ></div>
          {!appleReady && <p>Apple Pay is not supported on this device.</p>}
        </>
      )}

      {/* Card Form */}
      {selectedMethod === "card" && (
        <>
          <div
            id="card-container"
            ref={cardRef}
            style={{ minHeight: 60, marginBottom: 20 }}
          >
            {cardLoading && (
              <div style={{ textAlign: "center", padding: "30px 0" }}>
                <Spin tip="Loading payment form..." />
              </div>
            )}
          </div>
          <Button
            type="primary"
            onClick={handleCardPay}
            loading={loading}
            className="orange-pay-btn"
            disabled={!cardReady || loading}
          >
            Pay ${amount}
          </Button>
        </>
      )}
    </div>
  );
};

export default SquareCardPayment;
